import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { useParams } from "react-router-dom";
import Select from "react-select";

import { fetchVendorProducts } from "../redux/slices/productSlice";
import {
    getSingleBill,
    updateBill,
    clearSingleBill,
    clearBillState,
} from "../redux/slices/billSlice";

const EditVendorBill = ({ onClose, onUpdated }) => {

    const { billId } = useParams();
    const dispatch = useDispatch();

    const { bill, loading, error } = useSelector((state) => state.bill);
    const { vendorProducts } = useSelector((state) => state.product);
    const { user } = useSelector((state) => state.auth);

    const vendorId = user?.vendor?._id;

    const [customerName, setCustomerName] = useState("");
    const [customerMobile, setCustomerMobile] = useState("");
    const [customerAddress, setCustomerAddress] = useState("");

    const [selectedProducts, setSelectedProducts] = useState([]);
    const [items, setItems] = useState([]);

    const [paymentMode, setPaymentMode] = useState("Cash");

    // ✅ GST
    const [taxPercent, setTaxPercent] = useState(0);

    /* =========================
       FETCH PRODUCTS
    ========================= */
    useEffect(() => {
        if (vendorId) {
            dispatch(fetchVendorProducts({ vendorId, params: { page: 1, limit: 1000 } }));
        }
    }, [dispatch, vendorId]);

    /* =========================
       FETCH BILL
    ========================= */
    useEffect(() => {
        dispatch(getSingleBill(billId));

        return () => {
            dispatch(clearSingleBill());
            dispatch(clearBillState());
        };
    }, [billId, dispatch]);

    /* =========================
       PREFILL BILL DATA
    ========================= */
    useEffect(() => {
        if (bill && vendorProducts.length) {
            setCustomerName(bill.customerName || "");
            setCustomerMobile(bill.customerMobile || "");
            setCustomerAddress(bill.customerAddress || "");
            setPaymentMode(bill.paymentMode || "Cash");

            setTaxPercent(bill.taxPercent || 0);

            // ✅ Prefill items
            setItems(
                bill.items.map((i) => ({
                    product: i.product._id || i.product,
                    name: i.name,
                    price: i.price,
                    quantity: i.quantity,
                    total: i.total,
                }))
            );

            // ✅ Prefill react-select
            const preSelected = bill.items.map((i) => ({
                value: i.product._id || i.product,
                label: i.name,
                price: i.price,
            }));

            setSelectedProducts(preSelected);
        }
    }, [bill, vendorProducts]);

    /* =========================
       SYNC ITEMS WITH SELECT
    ========================= */
    useEffect(() => {
        const newItems = selectedProducts.map((p) => {
            const existing = items.find((i) => i.product === p.value);
            return (
                existing || {
                    product: p.value,
                    name: p.label,
                    price: p.price,
                    quantity: 1,
                    total: p.price,
                }
            );
        });
        setItems(newItems);
        // eslint-disable-next-line
    }, [selectedProducts]);

    /* =========================
       QUANTITY CHANGE
    ========================= */
    const handleQuantityChange = (index, value) => {
        const updated = [...items];
        updated[index].quantity = Number(value);
        updated[index].total = updated[index].price * updated[index].quantity;
        setItems(updated);
    };

    /* =========================
       CALCULATIONS
    ========================= */
    const subTotal = items.reduce((acc, i) => acc + i.total, 0);
    const taxAmount = (subTotal * taxPercent) / 100;
    const grandTotal = subTotal + taxAmount;

    const productOptions = vendorProducts.map((p) => ({
        value: p._id,
        label: p.name,
        price: p.price,
    }));

    /* =========================
       SUBMIT
    ========================= */
    const handleSubmit = async (e) => {
        e.preventDefault();

        const confirm = await Swal.fire({
            title: "Update Bill?",
            text: "Are you sure you want to update this bill?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, update",
        });

        if (!confirm.isConfirmed) return;

        const updatedBill = {
            customerName,
            customerMobile,
            customerAddress,
            items,
            paymentMode,
            taxPercent, // ✅ IMPORTANT
        };

        dispatch(updateBill({ billId, billData: updatedBill }))
            .unwrap()
            .then(() => {
                Swal.fire("Updated!", "Bill updated successfully", "success");
                onUpdated?.();
                onClose?.();
            })
            .catch((err) => {
                Swal.fire("Error", err || "Failed to update bill", "error");
            });
    };

    if (loading) return <p>Loading...</p>;
    if (error) return <p className="text-danger">{error}</p>;

    return (
        <div className="card">
            <div className="card-header">
                <h6>Edit Bill - {bill?.invoiceNumber}</h6>
            </div>

            <div className="card-body">
                <form onSubmit={handleSubmit}>
                    <div className="mb-2">
                        <label className="form-label">Customer Name</label>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            value={customerName}
                            onChange={(e) => setCustomerName(e.target.value)}
                        />
                    </div>

                    <div className="mb-2">
                        <label className="form-label">Customer Mobile</label>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            value={customerMobile}
                            onChange={(e) => setCustomerMobile(e.target.value)}
                        />
                    </div>

                    <div className="mb-2">
                        <label className="form-label">Customer Address</label>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            value={customerAddress}
                            onChange={(e) => setCustomerAddress(e.target.value)}
                        />
                    </div>

                    <div className="mb-2">
                        <label className="form-label">Select Products</label>
                        <Select
                            isMulti
                            options={productOptions}
                            value={selectedProducts}
                            onChange={setSelectedProducts}
                            isLoading={loading}
                        />
                    </div>

                    {items.length > 0 && (
                        <>
                            <h6>Items</h6>
                            <table className="table table-sm table-bordered mb-3">
                                <thead>
                                    <tr>
                                        <th>Product</th>
                                        <th>Price</th>
                                        <th>Qty</th>
                                        <th>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item, idx) => (
                                        <tr key={idx}>
                                            <td>{item.name}</td>
                                            <td>{item.price}</td>
                                            <td>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    className="form-control form-control-sm"
                                                    value={item.quantity}
                                                    onChange={(e) =>
                                                        handleQuantityChange(idx, e.target.value)
                                                    }
                                                />
                                            </td>
                                            <td>{item.total}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </>
                    )}

                    {/* ✅ GST */}
                    <div className="mb-2">
                        <label className="form-label">GST (%)</label>
                        <select
                            className="form-select form-select-sm"
                            value={taxPercent}
                            onChange={(e) => setTaxPercent(Number(e.target.value))}
                        >
                            <option value={0}>No GST</option>
                            <option value={5}>5%</option>
                            <option value={9}>9%</option>
                            <option value={18}>18%</option>
                            <option value={28}>28%</option>
                        </select>
                    </div>

                    {/* Summary */}
                    <div className="mb-2">
                        <small>Sub Total: ₹{subTotal.toFixed(2)}</small><br />
                        <small>GST: ₹{taxAmount.toFixed(2)}</small><br />
                        <strong>Grand Total: ₹{grandTotal.toFixed(2)}</strong>
                    </div>

                    <div className="mb-2">
                        <label className="form-label">Payment Mode</label>
                        <select
                            className="form-select form-select-sm"
                            value={paymentMode}
                            onChange={(e) => setPaymentMode(e.target.value)}
                        >
                            <option value="Cash">Cash</option>
                            <option value="UPI">UPI</option>
                            <option value="Card">Card</option>
                        </select>
                    </div>

                    <div className="d-flex justify-content-end">
                        <button type="submit" className="btn btn-primary btn-sm">
                            Update Bill
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditVendorBill;
